import { createMemo, For, Show } from "solid-js";
import {
  formatManeuverCellValue,
  gp50ManeuverColumnHeader,
  maneuverCellHeatClass,
  maneuverColumnScales,
  maneuverTableColumns,
  type PerformanceDriversManeuverRow,
} from "../../utils/performanceDriversManeuvers";
import {
  type Gp50ManeuverType,
} from "../../utils/gp50ManeuverCheatSheetColumns";

export interface PerformanceDriversManeuversTableProps {
  rows: PerformanceDriversManeuverRow[];
  loading?: boolean;
  error?: string | null;
  maneuverType: Gp50ManeuverType;
}

export default function PerformanceDriversManeuversTable(props: PerformanceDriversManeuversTableProps) {
  const columns = createMemo(() => maneuverTableColumns());
  const columnScales = createMemo(() => maneuverColumnScales(props.rows));
  const hasData = createMemo(() => props.rows.some((row) => !row.empty));
  const sectionTitle = createMemo(() =>
    props.maneuverType === "tack" ? "Maneuvers - Tacks" : "Maneuvers - Gybes"
  );

  return (
    <section class="performance-drivers-table-section performance-drivers-maneuvers-section">
      <h3 class="performance-drivers-section-title">{sectionTitle()}</h3>
      <p class="performance-drivers-section-explanation">
        Maneuver cheat sheet averages for the selected configuration group, by TWS band (same filtering as the
        Cheat Sheet: grade &gt; 1, lowest 10% loss within the IQR on Loss_total_tgt). Boat settings are from the
        maximum acceleration phase of the build. Colors compare values down each column across TWS rows.
      </p>

      <Show when={props.loading}>
        <p class="performance-drivers-empty">Loading maneuver data…</p>
      </Show>

      <Show when={!props.loading && props.error}>
        <p class="performance-drivers-empty">{props.error}</p>
      </Show>

      <Show when={!props.loading && !props.error}>
        <Show
          when={hasData()}
          fallback={
            <p class="performance-drivers-empty cheat-sheet-empty-message">
              No maneuver data for this configuration and point of sail. Try another selection or wind band.
            </p>
          }
        >
          <div class="data-table-container">
            <div class="scrollable-container">
              <table class="maneuvers-table performance-drivers-table performance-drivers-maneuvers-table">
                <thead>
                  <tr>
                    <th class="performance-drivers-col-tws centered">TWS</th>
                    <For each={columns()}>
                      {(col) => <th class="centered">{gp50ManeuverColumnHeader(col)}</th>}
                    </For>
                  </tr>
                </thead>
                <tbody>
                  <For each={props.rows}>
                    {(row) => (
                      <tr class={row.empty ? "performance-drivers-row-skipped" : undefined}>
                        <td class="performance-drivers-col-tws centered">{row.twsBin}</td>
                        <For each={columns()}>
                          {(col) => {
                            const val =
                              col.toLowerCase() === "record_count" ? row.recordCount : row.values[col] ?? null;
                            const heatClass = row.empty
                              ? "performance-drivers-cell-empty"
                              : maneuverCellHeatClass(val, col, columnScales());
                            return <td class={heatClass}>{formatManeuverCellValue(col, val)}</td>;
                          }}
                        </For>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </div>
        </Show>
      </Show>
    </section>
  );
}
